import { Box, Typography, useTheme } from '@mui/material';

import { BannerModel } from '@/logic/services';
import { BookmarkName } from '../shared/bookmark-name';
import { FC } from 'react';
// Legacy image for the same reason as the rest of the app: `layout`/`objectFit`
// preserve the Next 12 behaviour after the Next 15 upgrade.
import Image from 'next/legacy/image';
import SliderItemInfo from './slider-item-info';
import { pxToRem } from '@/theme/typography';
import { mediaImageLoader, supportsMediaResize } from '@/logic/helpers/media-image';

interface BackgroundsSliderItemProps {
  banner: BannerModel;
  /**
   * Only the first slide is eager: it is the desktop LCP element. The rest sit
   * behind a fade transition and stay lazy, so the hero does not compete with
   * covers nobody has scrolled to.
   */
  priority?: boolean;
}

// The cover used to be a CSS `background-image` pointing straight at the CMS
// original — 656 KB of JPEG, never resized, because CSS backgrounds never pass
// through next/image.
//
// Rendering it as a real <Image layout='fill'> instead gets a srcset from the
// media CDN's widths (media-image.ts): the browser picks the width from the
// viewport, and the `priority` preload carries the same srcset. The gradients
// that used to be stacked in the same `background` shorthand are now an overlay
// element on top of the image.
const OVERLAY_GRADIENTS = `
  linear-gradient(180deg, rgba(0, 0, 0, 0.87) 7.33%, rgba(0, 0, 0, 0) 47.54%),
  linear-gradient(256.27deg, #171A1F 0%, rgba(23, 26, 31, 0) 107.57%)`;

const BackgroundsSliderItem: FC<BackgroundsSliderItemProps> = ({
  banner,
  priority = false,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        overflow: 'hidden',
        paddingX: pxToRem(156),
        paddingTop: pxToRem(54),
        color: theme.palette.common.white,
      }}
    >
      <Image
        src={banner.coverUrl}
        // Decorative: the film title, rating and CTA are already rendered as
        // text by SliderItemInfo, so announcing the cover would only repeat
        // them.
        alt=''
        layout='fill'
        objectFit='cover'
        objectPosition='center top'
        priority={priority}
        loader={mediaImageLoader}
        unoptimized={!supportsMediaResize(banner.coverUrl)}
      />

      {/* The gradients previously sat in the same `background` shorthand as the
          cover. As a sibling they need to be explicitly stacked over it — the
          image next/legacy/image renders is itself absolutely positioned. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          background: OVERLAY_GRADIENTS,
          pointerEvents: 'none',
        }}
      />

      <Box
        sx={{
          position: 'relative',
          display: {
            mobile: 'none',
            laptop: 'block',
          },
        }}
      >
        <SliderItemInfo
          banner={banner}
          primaryColor={theme.palette.common.white}
          secondaryColor={theme.palette.common.white}
        />
      </Box>
      {banner.bannerableType === 'FILM' && banner.film.bookmark && (
        <Typography
          component='span'
          sx={{
            position: 'absolute',
            top: pxToRem(62),
            left: pxToRem(0),
            color: banner.film.bookmark.fontColor,
            backgroundColor: banner.film.bookmark.backgroundColor,
            paddingY: pxToRem(15.5),
            paddingX: pxToRem(31),
            borderTopRightRadius: pxToRem(20),
            borderBottomRightRadius: pxToRem(20),
          }}
        >
          <BookmarkName name={banner.film.bookmark.name} />
        </Typography>
      )}
    </Box>
  );
};

export default BackgroundsSliderItem;
